import { type I18nContent, KnockFeedProvider, KnockGuideProvider, KnockProvider } from '@knocklabs/react';
import '@knocklabs/react/dist/index.css';
import type { ReactNode } from 'react';

/**
 * Tradução pt-BR do chrome dos componentes de feed/guide do Knock (`@knocklabs/react`).
 *
 * O Knock só publica os locales `en` e `de`, então o pt-BR precisa ser fornecido pelo client
 * via prop `i18n` do `KnockProvider`. É `Partial<Translations>` — só as chaves que o feed
 * in-app usa; o resto cai no default em inglês do Knock. Não confundir com as traduções
 * server-side (CLI `translations/<locale>/...json`), que traduzem o conteúdo dos templates.
 */
const KNOCK_PT_BR_I18N: I18nContent = {
  locale: 'pt-BR',
  translations: {
    notifications: 'Notificações',
    markAllAsRead: 'Marcar todas como lidas',
    archiveNotification: 'Arquivar esta notificação',
    archiveRead: 'Arquivar lidas',
    emptyFeedTitle: 'Nenhuma notificação ainda',
    emptyFeedBody: 'Avisaremos quando tivermos algo novo para você.',
    all: 'Todas',
    unread: 'Não lidas',
    read: 'Lidas',
    unseen: 'Não vistas',
  },
};

type KnockProvidersProps = {
  apiKey: string;
  userId: string;
  fleetId?: string;
  language?: string;
  feedChannelId?: string;
  guideChannelId?: string;
  children: ReactNode;
};

/**
 * Composição dos providers de UI in-app do Knock (feed + guide), com o design system da Cobli.
 *
 * Recebe os valores já resolvidos (api key, user, tenant, channels, idioma) — o gating por
 * feature flag / sessão / ambiente fica na app (`apps/dashboard` KnockInAppProvider), que monta
 * esta composição uma única vez no topo da árvore privada para que sino do feed e modal de guide
 * compartilhem um único client + websocket do Knock durante toda a sessão.
 *
 * Cada provider aninhado é condicionado ao seu próprio channel id, e o locale do Knock acompanha
 * o idioma do app: pt-BR usa `KNOCK_PT_BR_I18N`, os demais caem no default `en` do Knock.
 */
const KnockProviders = ({ apiKey, userId, fleetId, language, feedChannelId, guideChannelId, children }: KnockProvidersProps) => {
  const knockI18n = language?.toLowerCase().startsWith('pt') ? KNOCK_PT_BR_I18N : undefined;

  let tree = <>{children}</>;

  if (guideChannelId) {
    tree = (
      <KnockGuideProvider channelId={guideChannelId} readyToTarget targetParams={{ tenant: fleetId }}>
        {tree}
      </KnockGuideProvider>
    );
  }

  if (feedChannelId) {
    tree = (
      // Assinatura ÚNICA do feed do Knock, compartilhada por todas as surfaces (sino do header, card
      // flutuante, toast) — antes cada surface abria seu próprio socket. As opções são o superset que
      // todas precisam:
      // - `mode: 'rich'` mantém arrays/objetos aninhados em `data` (risk_factors, suggestions, metadata);
      //   o default `compact` os remove e o feed do header divergiria do card flutuante.
      // - `archived: 'exclude'` / `status: 'all'` / `page_size: 10` são o que o card+toast precisavam pra
      //   ressurgir eventos recentes não-arquivados; aplicados aqui valem também pro sino (verificar layout).
      <KnockFeedProvider feedId={feedChannelId} defaultFeedOptions={{ tenant: fleetId, mode: 'rich', archived: 'exclude', status: 'all', page_size: 10 }}>
        {tree}
      </KnockFeedProvider>
    );
  }

  return (
    <KnockProvider apiKey={apiKey} user={{ id: userId }} i18n={knockI18n}>
      {tree}
    </KnockProvider>
  );
};

export default KnockProviders;
